'use client'

import { debounce, throttle } from 'lodash/fp'
import { useCallback, useEffect } from 'react'

const STORAGE_KEY = `tf_last_activity_${
  process.env.NODE_ENV === 'development' ? 'dev' : 'prod'
}`

// Interval to check last activity time from the local storage
const INACTIVITY_CHECK_INTERVAL = 5000
// 30 minutes
const INACTIVITY_TIMEOUT = 30 * 60 * 1000

export const useInactivityLogout = () => {
  // Debounce logout to make sure it gets called only once every 2 seconds
  // and we are not bombarded with redirects
  const logOut = debounce(2000, () => {
    localStorage.removeItem(STORAGE_KEY)
    window.location.href = '/auth/logout'
  })

  const resetTimer = useCallback((allowCreate?: boolean) => {
    const last = localStorage.getItem(STORAGE_KEY)
    // If the key is not found and is not allowed to be created i-e after the first load
    // Then this means that the session was logged out due to inactivity
    // Might happen from another tab.
    // In that case trigger a logout in the current tab too.
    if (!last && !allowCreate) {
      logOut()
      return
    }
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
  }, [])

  const checkInactivity = useCallback(() => {
    const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? '0')

    if (!last || Date.now() - last > INACTIVITY_TIMEOUT) {
      logOut()
    }
  }, [])

  useEffect(() => {
    // On the first load, see if the STORAGE_KEY exists
    // If not then set it
    const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? '')
    if (isNaN(last)) {
      resetTimer(true)
    }
    // checkInactivity()

    // Create a throttled version of resetTime to limit the number of writes
    const onActivity = throttle(5000, () => {
      resetTimer()
    })

    const events = ['mousemove', 'keydown', 'scroll', 'click']
    events.forEach((event) => window.addEventListener(event, onActivity))
    onActivity()

    const interval = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL)

    return () => {
      events.forEach((event) => window.removeEventListener(event, onActivity))
      clearInterval(interval)
    }
  }, [])
}

export function InactivityLogoutController() {
  useInactivityLogout()
  return null
}
